import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
import ConfirmDialog from './components/ConfirmDialog';
import IdleWarningDialog from './components/IdleWarningDialog';
import useIdleLogout from './hooks/useIdleLogout';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import VATPage from './pages/VATPage';
import VATMonthlyPage from './pages/VATMonthlyPage';
import InvoicesPage from './pages/InvoicesPage';
import TasksPage from './pages/TasksPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';
import BriseviPage from './pages/BriseviPage';
import VodaPage from './pages/VodaPage';
import HranaPage from './pages/HranaPage';
import DuvanskiPage from './pages/DuvanskiPage';
import KaznenaPage from './pages/KaznenaPage';
import PrijavaTragovinePage from './pages/PrijavaTragovinePage';
import DnevnikPage from './pages/DnevnikPage';
import HronologijaPage from './pages/HronologijaPage';
import PrigovorPage from './pages/PrigovorPage';
import ProdajnePogodnostiPage from './pages/ProdajnePogodnostiPage';
import OdlukaOdmorPage from './pages/OdlukaOdmorPage';
import OdlukaSmjenePage from './pages/OdlukaSmjenePage';
import PrijavaZanatstvaPage from './pages/PrijavaZanatstvaPage';
import CestitkePage from './pages/CestitkePage';
import IRMSZahtjeviPage from './pages/IRMSZahtjeviPage';
import PorodiljskoPage from './pages/PorodiljskoPage';
import BolovanjePage from './pages/BolovanjePage';
import FizickaLicaPage from './pages/FizickaLicaPage';
import RegistracijaHranePage from './pages/RegistracijaHranePage';
import UgostiteljskaPage from './pages/UgostiteljskaPage';
import CTRPage from './pages/CTRPage';
import CTRRestoraniPage from './pages/CTRRestoraniPage';
import KupalistaPage from './pages/KupalistaPage';
import UgostiteljskaOpstinaPage from './pages/UgostiteljskaOpstinaPage';
import WorkspacesPage from './pages/WorkspacesPage';
import BackupPage from './pages/BackupPage';
import PrijavaRadnikaPage from './pages/PrijavaRadnikaPage';
import OdjavaRadnikaPage from './pages/OdjavaRadnikaPage';
import MobingPage from './pages/MobingPage';
import UgovoriPage from './pages/UgovoriPage';
import TokeniPage from './pages/TokeniPage';
import IOPPDPage from './pages/IOPPDPage';
import PunomocjeCoreitPage from './pages/PunomocjeCoreitPage';
import RsvPage from './pages/RsvPage';
import GodisnjeObavezePage from './pages/GodisnjeObavezePage';
import SentEmailsPage from './pages/SentEmailsPage';
import SentViberPage from './pages/SentViberPage';
import ViberInboundPage from './pages/ViberInboundPage';
import ViberChatPage from './pages/ViberChatPage';
import MainLayout from './components/layouts/MainLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-retry GET requests on transient failures (network errors, 5xx, 408, 429).
// POST/PUT/DELETE/PATCH are NEVER retried to avoid duplicate writes.
// Max 2 retries with backoff: 300ms, 800ms. After all retries fail, error
// propagates normally to the caller and gets shown to the user.
const RETRY_DELAYS_MS = [300, 800];
const isRetryableStatus = (status) => status === 408 || status === 429 || (status >= 500 && status < 600);
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config || {};
    const method = (cfg.method || 'get').toLowerCase();
    const status = error.response?.status;
    const isNetworkError = !error.response;
    const retryable = method === 'get' && (isNetworkError || (status && isRetryableStatus(status)));

    if (retryable) {
      cfg.__retryCount = cfg.__retryCount || 0;
      if (cfg.__retryCount < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[cfg.__retryCount]);
        cfg.__retryCount += 1;
        try { return await api.request(cfg); } catch (e) { /* fall through to final handling */ }
      }
    }

    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Network error (backend not reachable, even after retries) — DO NOT log out; show a persistent warning
    // so the user knows their data was NOT saved and can retry without losing form content.
    if (isNetworkError) {
      try {
        toast.error(
          'Server trenutno nije dostupan — vaš unos NIJE SAČUVAN. Sačekajte par sekundi i probajte ponovo.',
          { duration: 8000, id: 'net-error' }
        );
      } catch (_) { /* ignore */ }
    }
    // Normalize FastAPI 422 Pydantic validation errors (array of {type,loc,msg,input,ctx})
    // so consumers can safely render `error.response.data.detail` as a string in toasts/JSX.
    const d = error.response?.data?.detail;
    if (Array.isArray(d)) {
      try {
        error.response.data.detail = d
          .map((e) => {
            if (typeof e === 'string') return e;
            const field = Array.isArray(e?.loc) ? e.loc.filter(x => x !== 'body').join('.') : '';
            const msg = e?.msg || 'Neispravna vrijednost';
            return field ? `${field}: ${msg}` : msg;
          })
          .join('; ');
      } catch (_) {
        error.response.data.detail = 'Greska validacije';
      }
    } else if (d && typeof d === 'object') {
      error.response.data.detail = d.msg || JSON.stringify(d);
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  // Auto-logout after 5 minutes of inactivity (warning shown 30s before).
  const handleIdleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Signal LoginPage to also wipe cached "remember me" email — security:
    // if the user walked away from the machine, we don't want their email
    // (which is half of the credentials) visible to anyone who finds it.
    localStorage.setItem('auto_logout_triggered', '1');
    // Ask the browser's Credential Management API to NOT silently auto-fill
    // credentials on the next page (best-effort — supported in modern Chrome).
    try {
      if (navigator.credentials && navigator.credentials.preventSilentAccess) {
        navigator.credentials.preventSilentAccess();
      }
    } catch (e) { /* ignore */ }
    setIsAuthenticated(false);
    toast.error('Automatski ste odjavljeni zbog 5 minuta neaktivnosti.', { duration: 6000 });
    // Force navigation to login (full reload to clear any stale state)
    window.location.href = '/login';
  };
  const { showWarning, secondsLeft, stayActive } = useIdleLogout(isAuthenticated, handleIdleLogout);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(!!localStorage.getItem('token'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Subscribe to push notifications when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const subscribePush = async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          await api.post('/push/subscribe', existing.toJSON());
          return;
        }
        const res = await api.get('/push/vapid-key');
        const publicKey = res.data.publicKey;
        if (!publicKey) return;
        const sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        await api.post('/push/subscribe', sub.toJSON());
      } catch (err) {
        console.log('Push subscription skipped:', err.message);
      }
    };
    subscribePush();
  }, [isAuthenticated]);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <ConfirmDialog />
      <IdleWarningDialog
        open={showWarning && isAuthenticated}
        secondsLeft={secondsLeft}
        onStay={stayActive}
        onLogoutNow={handleIdleLogout}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <MainLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/vat" element={<VATPage />} />
                    <Route path="/vat-monthly" element={<VATMonthlyPage />} />
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/tasks" element={<TasksPage />} />
                    <Route path="/zahtjev-brisevi" element={<BriseviPage />} />
                    <Route path="/zahtjev-voda" element={<VodaPage />} />
                    <Route path="/zahtjev-hrana" element={<HranaPage />} />
                    <Route path="/zahtjev-duvanski" element={<DuvanskiPage />} />
                    <Route path="/zahtjev-kaznena" element={<KaznenaPage />} />
                    <Route path="/zahtjev-prijava-trgovine" element={<PrijavaTragovinePage />} />
                    <Route path="/zahtjev-prijava-zanatstva" element={<PrijavaZanatstvaPage />} />
                    <Route path="/zahtjev-prigovor" element={<PrigovorPage />} />
                    <Route path="/zahtjev-pogodnosti" element={<ProdajnePogodnostiPage />} />
                    <Route path="/zahtjev-odmor" element={<OdlukaOdmorPage />} />
                    <Route path="/zahtjev-smjene" element={<OdlukaSmjenePage />} />
                    <Route path="/zahtjev-registracija-hrane" element={<RegistracijaHranePage />} />
                    <Route path="/zahtjev-ugostiteljska" element={<UgostiteljskaPage />} />
                    <Route path="/zahtjev-ctr" element={<CTRPage />} />
                    <Route path="/zahtjev-ctr-restorani" element={<CTRRestoraniPage />} />
                    <Route path="/zahtjev-kupalista" element={<KupalistaPage />} />
                    <Route path="/zahtjev-ug-opstina" element={<UgostiteljskaOpstinaPage />} />
                    <Route path="/workspaces" element={<WorkspacesPage />} />
                    <Route path="/backup" element={<BackupPage />} />
                    <Route path="/mobing" element={<MobingPage />} />
                    <Route path="/ugovori" element={<UgovoriPage />} />
                    <Route path="/prijava-radnika" element={<PrijavaRadnikaPage />} />
                    <Route path="/odjava-radnika" element={<OdjavaRadnikaPage />} />
                    <Route path="/dnevnik" element={<DnevnikPage />} />
                    <Route path="/hronologija" element={<HronologijaPage />} />
                    <Route path="/cestitke" element={<CestitkePage />} />
                    <Route path="/irms-zahtjevi" element={<IRMSZahtjeviPage />} />
                    <Route path="/porodiljsko" element={<PorodiljskoPage />} />
                    <Route path="/bolovanje" element={<BolovanjePage />} />
                    <Route path="/fizicka-lica" element={<FizickaLicaPage />} />
                    <Route path="/tokeni" element={<TokeniPage />} />
                    <Route path="/ioppd" element={<IOPPDPage />} />
                    <Route path="/zahtjev-punomocje-coreit" element={<PunomocjeCoreitPage />} />
                    <Route path="/rsv" element={<RsvPage />} />
                    <Route path="/godisnje-obaveze" element={<GodisnjeObavezePage />} />
                    <Route path="/sent-emails" element={<SentEmailsPage />} />
                    <Route path="/sent-viber" element={<SentViberPage />} />
                    <Route path="/viber-inbox" element={<ViberInboundPage />} />
                    <Route path="/viber-chat" element={<ViberChatPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </MainLayout>
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
